import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import { HttpClient, NonNullablePaths } from '@wix/sdk-types';
import * as ambassadorWixPaymentsMcpV1WixPaymentsAccount from './payments-mcp-v1-wix-payments-account-wix-payments-account.http.js';

/** An account that enables a Wix site to process customer payments. */
export interface WixPaymentsAccount {
  /**
   * ID of the Wix Payments account.
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /**
   * First name of the Wix Payments account owner.
   * @maxLength 500
   */
  firstName?: string | null;
  /**
   * Last name of the Wix Payments account owner.
   * @maxLength 500
   */
  lastName?: string | null;
  /**
   * Whether the account owner has accepted the
   * [Wix Payments Terms of Service](https://www.wix.com/about/terms-of-payments).
   * The account owner must accept these terms for the account to be created
   * successfully. If the terms aren't accepted, the Connect Wix Payments Account
   * call fails.
   * @deprecated Whether the account owner has accepted the
   * [Wix Payments Terms of Service](https://www.wix.com/about/terms-of-payments).
   * The account owner must accept these terms for the account to be created
   * successfully. If the terms aren't accepted, the Connect Wix Payments Account
   * call fails.
   * @replacedBy terms_of_service_acceptance
   * @targetRemovalDate 2026-07-31
   */
  tosAccepted?: boolean | null;
  termsOfServiceAcceptance?: TermsOfServiceAcceptance;
  /**
   * email of the Wix Payments account owner.
   * @format EMAIL
   */
  email?: string | null;
  /**
   * Brief summary of the types of products and services offered for sale through the Wix Payments account.
   * For example, "Sportswear and sport accessories".
   * @maxLength 500
   */
  productDescription?: string | null;
  /**
   * Country code.
   * @format COUNTRY
   */
  country?: string | null;
  /** @maxLength 50 */
  softDescriptor?: string | null;
  /** @format WEB_URL */
  businessUrl?: string | null;
}

export interface TermsOfServiceAcceptance {
  /** @maxLength 45 */
  ip?: string;
}

export interface ConnectWixPaymentsAccountRequest {
  /** Wix Payments account to connect to the site. */
  account: WixPaymentsAccount;
}

export interface ConnectWixPaymentsAccountResponse {
  /** Connected Wix Payments account. */
  account?: WixPaymentsAccount;
}

export interface ConnectWixPaymentsAccountBase44Request {
  /** Wix Payments account to connect to the site. */
  account: WixPaymentsAccount;
  /**
   * MetaSite id
   * @format GUID
   */
  siteId?: string | null;
  /**
   * User id
   * @format GUID
   */
  userId?: string | null;
  /**
   * App id
   * @maxLength 32
   */
  appId?: string | null;
  base44App?: Base44AppData;
}

export interface Base44AppData {
  /**
   * App id
   * @maxLength 32
   */
  appId?: string | null;
  /**
   * App name
   * @maxLength 512
   */
  appName?: string | null;
  /**
   * App url
   * @format WEB_URL
   */
  appUrl?: string | null;
  /**
   * email of the Base app owner.
   * @format EMAIL
   */
  ownerEmail?: string | null;
  /**
   * Country code.
   * @format COUNTRY
   */
  country?: string | null;
}

export interface ConnectWixPaymentsAccountBase44Response {
  /** Connected Wix Payments account. */
  account?: WixPaymentsAccount;
  base44Metadata?: Base44Metadata;
}

export interface Base44Metadata {
  /**
   * MetaSite ID
   * @format GUID
   */
  siteId?: string;
  /**
   * Webhook public key for verifying callbacks
   * @maxLength 500
   */
  webhookPublicKey?: string;
  /**
   * App secret for authentication
   * @maxLength 500
   */
  appSecret?: string;
  /**
   * API key for subsequent calls
   * @maxLength 500
   */
  apiKey?: string;
}

export interface SetupBase44SiteRequest {
  /**
   * MetaSite ID. If not provided, a new metasite is created.
   * @format GUID
   */
  siteId?: string | null;
  /**
   * User ID. If not provided, extracted from the authentication token.
   * @format GUID
   */
  userId?: string | null;
  /**
   * App ID
   * @maxLength 32
   */
  appId?: string | null;
  /** Base44 app data */
  base44App?: Base44AppData;
}

export interface SetupBase44SiteResponse {
  /**
   * MetaSite ID of the created or existing site
   * @format GUID
   */
  siteId?: string;
  /**
   * API key for subsequent calls
   * @maxLength 500
   */
  apiKey?: string;
}

/** @docsIgnore */
export type ConnectWixPaymentsAccountBase44ApplicationErrors = {
  code?: 'MISSING_TERMS_OF_SERVICE';
  description?: string;
  data?: Record<string, any>;
};

/**
 * Connects a Wix Payments account to the site.
 *
 *
 * The call fails if:
 * - The site's location
 * ([SDK](https://dev.wix.com/docs/sdk/backend-modules/business-tools/locations/introduction) | [REST](https://dev.wix.com/docs/rest/business-management/locations/introduction))
 * is outside the USA.
 * - A Wix Payments account is already connected to the site.
 * - You don't specify `{"tosAccepted": true}`.
 *
 * The connected Wix Payments account has the following limitations, which site
 * owners can manage in their dashboard:
 *
 * - By default, customers can only pay via credit card, Apple Pay, or Google Pay.
 * - To receive payments, site owners must complete onboarding to their Wix
 * Payments account in the site's dashboard.
 * @param account - Wix Payments account to connect to the site.
 * @public
 * @documentationMaturity preview
 * @requiredField account
 * @requiredField account.firstName
 * @requiredField account.lastName
 * @requiredField account.tosAccepted
 * @permissionId CASHIER.PROVIDER_ACCOUNTS_CREATE
 * @applicableIdentity APP
 * @fqn wix.payments.mcp.v1.WixPaymentsAccountService.ConnectWixPaymentsAccount
 */
export async function connectWixPaymentsAccount(
  account: NonNullablePaths<
    WixPaymentsAccount,
    `firstName` | `lastName` | `tosAccepted`,
    2
  >
): Promise<
  NonNullablePaths<
    ConnectWixPaymentsAccountResponse,
    `account.termsOfServiceAcceptance.ip`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ account: account });

  const reqOpts =
    ambassadorWixPaymentsMcpV1WixPaymentsAccount.connectWixPaymentsAccount(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { account: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['account']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Connects a Wix Payments account to the site.
 *
 *
 * The call fails if:
 * - The site's location
 * ([SDK](https://dev.wix.com/docs/sdk/backend-modules/business-tools/locations/introduction) | [REST](https://dev.wix.com/docs/rest/business-management/locations/introduction))
 * is outside the USA.
 * - A Wix Payments account is already connected to the site.
 * - You don't specify `{"tosAccepted": true}`.
 *
 * The connected Wix Payments account has the following limitations, which site
 * owners can manage in their dashboard:
 *
 * - By default, customers can only pay via credit card, Apple Pay, or Google Pay.
 * - To receive payments, site owners must complete onboarding to their Wix
 * Payments account in the site's dashboard.
 * @param account - Wix Payments account to connect to the site.
 * @public
 * @documentationMaturity preview
 * @requiredField account
 * @requiredField account.email
 * @requiredField account.firstName
 * @requiredField account.lastName
 * @requiredField account.termsOfServiceAcceptance
 * @permissionId PAYMENTS.CONNECT_PAYMENTS
 * @fqn wix.payments.mcp.v1.WixPaymentsAccountService.ConnectWixPaymentsAccountBase44
 */
export async function connectWixPaymentsAccountBase44(
  account: NonNullablePaths<
    WixPaymentsAccount,
    `email` | `firstName` | `lastName` | `termsOfServiceAcceptance`,
    2
  >,
  options?: ConnectWixPaymentsAccountBase44Options
): Promise<
  NonNullablePaths<
    ConnectWixPaymentsAccountBase44Response,
    | `account.termsOfServiceAcceptance.ip`
    | `base44Metadata.siteId`
    | `base44Metadata.webhookPublicKey`
    | `base44Metadata.appSecret`
    | `base44Metadata.apiKey`,
    4
  > & {
    __applicationErrorsType?: ConnectWixPaymentsAccountBase44ApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    account: account,
    siteId: options?.siteId,
    userId: options?.userId,
    appId: options?.appId,
    base44App: options?.base44App,
  });

  const reqOpts =
    ambassadorWixPaymentsMcpV1WixPaymentsAccount.connectWixPaymentsAccountBase44(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          account: '$[0]',
          siteId: '$[1].siteId',
          userId: '$[1].userId',
          appId: '$[1].appId',
          base44App: '$[1].base44App',
        },
        singleArgumentUnchanged: false,
      },
      ['account', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface ConnectWixPaymentsAccountBase44Options {
  /**
   * MetaSite id
   * @format GUID
   */
  siteId?: string | null;
  /**
   * User id
   * @format GUID
   */
  userId?: string | null;
  /**
   * App id
   * @maxLength 32
   */
  appId?: string | null;
  base44App?: Base44AppData;
}

/**
 * Sets up a Base44 site with an API key, without connecting a Wix Payments account.
 *
 * This endpoint:
 * - Creates or publishes the metasite
 * - Installs required apps
 * - Adds the "accept_payments" premium feature
 * - Creates an API key for the site
 * - Persists the Base44 app record
 *
 * Use `ConnectWixPaymentsAccountBase44` to also connect a Wix Payments account.
 * @public
 * @documentationMaturity preview
 * @permissionId PAYMENTS.CONNECT_PAYMENTS
 * @fqn wix.payments.mcp.v1.WixPaymentsAccountService.SetupBase44Site
 */
export async function setupBase44Site(
  options?: SetupBase44SiteOptions
): Promise<NonNullablePaths<SetupBase44SiteResponse, `siteId` | `apiKey`, 2>> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    siteId: options?.siteId,
    userId: options?.userId,
    appId: options?.appId,
    base44App: options?.base44App,
  });

  const reqOpts =
    ambassadorWixPaymentsMcpV1WixPaymentsAccount.setupBase44Site(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          siteId: '$[0].siteId',
          userId: '$[0].userId',
          appId: '$[0].appId',
          base44App: '$[0].base44App',
        },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface SetupBase44SiteOptions {
  /**
   * MetaSite ID. If not provided, a new metasite is created.
   * @format GUID
   */
  siteId?: string | null;
  /**
   * User ID. If not provided, extracted from the authentication token.
   * @format GUID
   */
  userId?: string | null;
  /**
   * App ID
   * @maxLength 32
   */
  appId?: string | null;
  /** Base44 app data */
  base44App?: Base44AppData;
}
